import moment from "moment";
import _ from 'lodash';

import Timesheet from "./Timesheet";
import { ApiMixin } from "./ApiMixin";
import { AccountApi } from "../../util/api/AccountApi";

import { Alert } from "../Alert";


//Returns the account object, plus some helper functions

class Account {
    id: null;
    employerId: null;
    studentId: null;
    supervisorId: null;

    hasAcceptedAwardOffer: false;

    timesheetIds: [];
    accountStatusChanges: [];
    secondarySupervisorIds: [];

    startDate: "";
    endDate: "";

    fopal: "";

    payRate: 0;

    appStore: null;

    constructor(account = {}, appStore, shouldParseValues = false) {


        this.parseValues = this.parseValues.bind(this);
        //Cleans the constructor fields of Formik formatting
        if (shouldParseValues) {
            this.parseValues(account);
        }

        this.appStore = appStore;

        //Adds any default values passed by the developer 
        _.defaults(this, account);
    }

    mutate(account = {}) {

        _.assign(this, _.pick(account, _.keys(this)));

    }


    //This function is callsed if we need to parse values from a Formik field.
    parseValues(item) {

        if (item.id) {
            this.id = parseInt(item.id);
        }

        this.employerId = parseInt(item.employerId)
        this.studentId = parseInt(item.studentId)
        this.supervisorId = parseInt(item.supervisorId)
        this.payRate = parseInt(item.payRate)

        this.startDate = new Date(item.startDate).toISOString()
        this.endDate = new Date(item.endDate).toISOString()
        this.secondarySupervisorIds = item.secondarySupervisorIds;
        this.fopal = item.fopal;

        this.hasAcceptedAwardOffer = item.hasAcceptedAwardOffer === "true";

    }


    // Simplifies the display of supervisor names, a common use case.  
    getSecondarySupervisorNames() {
        let tempAccount = this;

        if (!tempAccount.secondarySupervisors) {
            tempAccount.include(["secondarySupervisor"]);
        }


        if (tempAccount.secondarySupervisors && tempAccount.secondarySupervisors.length > 1) {
            return tempAccount.secondarySupervisors.reduce((supervisorA, supervisorB, index) => {
                //console.log("SupervisorA: " + supervisorA + " SupervisorB: " + supervisorB)
                if (index == 1) {
                    return supervisorB.name;
                }
                return supervisorA + ", " + supervisorB.name;
            });
        }

        else if (tempAccount.secondarySupervisors.length === 1) {
            return tempAccount.secondarySupervisors[0].name;
        }
        else {
            return "N/A";
        }
    }

    // Include related data   
    include(includedObjects) {

        if (includedObjects.includes("student")) {
            const studentId = this.studentId;

            // Get student by id 
            const students = this.appStore.students.collection.filter((student) => student.id == studentId)

            if (students) {
                // Only one student should be returned, but .filter() returns an array, so .pop() is used. 
                this.student = students.pop();
            }
        }

        if (includedObjects.includes("employer")) {
            const employerId = this.employerId;

            // Get employer by id 
            const employers = this.appStore.employers.collection.filter((employer) => employer.id == employerId);
            if (employers)
                this.employer = employers.pop();

        }

        if (includedObjects.includes("supervisor")) {
            const supervisorId = this.supervisorId;
            const supervisor = this.appStore.supervisors.getById(supervisorId);

            if (supervisor)
                this.supervisor = supervisor
        }

        if (includedObjects.includes("secondarySupervisor")) {
            const secondarySupervisorIds = this.secondarySupervisorIds;
            const secondarySupervisors = this.appStore.supervisors.collection.filter((supervisor) => secondarySupervisorIds.includes(supervisor.id));

            if (secondarySupervisors)
                this.secondarySupervisors = secondarySupervisors;
        }

        if (includedObjects.includes("timesheet") || includedObjects.includes("timestamp")) {
            const timesheetIds = this.timesheetIds;

            // Get timesheets by Id
            const timesheets = this.appStore.timesheets.collection.filter((timesheet) => timesheetIds.includes(timesheet.id));
            if (timesheets.length > 0) {
                this.timesheets = timesheets
            }
        }

        if (includedObjects.includes("timestamp") && this.timesheets) {
            // Get timestamp ids into one array
            const timestampIds = this.timesheets.map(timesheet => timesheet.timestampIds).flat();
            // Get timestamps by timesheet id
            const timestamps = this.appStore.timestamps.collection.filter((timestamp) => timestampIds.includes(timestamp.id));
            if (timestamps)
                this.timestamps = timestamps
        }

        return this;
    }

    getPayPeriods() {

        let payPeriods = {
            "unsubmitted": [],
            "pending": [],
            "completed": [],
        }

        const accountWithTimesheets = this.include(["timesheet"]);


        const currentPayPeriod = moment(this.appStore.validTimeslots[0].endDay);

        //Figure out all the valid pay period slots for this account,
        //Get start date, set it to 0:00 and eastern time zone.
        const payPeriod = moment(this.startDate).startOf("day")

        //Find the following Friday 
        if (payPeriod.isoWeekday() == 5) { //5 is friday  
            payPeriod.add(1, "weeks");
        }
        else {
            while (payPeriod.isoWeekday() !== 5) {
                payPeriod.add(1, "days");
            }
        }

        const allPayPeriods = [];
        const endDate = moment(this.endDate).startOf("day");
        let beforePayPeriod = true;
        while (beforePayPeriod && payPeriod < endDate) {

            let newTimesheet = new Timesheet({
                weekEndDate: payPeriod.toDate(),
                weekEndDateObject: payPeriod.clone(),
                weekStartDateObject: payPeriod.clone().subtract(6, "days"),
            });


            allPayPeriods.push(newTimesheet);


            if (payPeriod.isSame(currentPayPeriod)) {
                beforePayPeriod = false;
            }

            payPeriod.add(1, "weeks");
        }


        //function for sorting the pay periods by date
        const sortByDate = function (a, b) {
            return a.weekEndDateObject > b.weekEndDateObject
        }
        if (accountWithTimesheets.timesheets) {

            payPeriods["completed"] = accountWithTimesheets.timesheets.filter((timesheet) => {
                return (
                    //timesheet.isFinal === true &&
                    timesheet.isCertified === true
                )
            }).sort(sortByDate);

            payPeriods["pending"] = accountWithTimesheets.timesheets.filter((timesheet) => {
                return (
                    timesheet.isFinal === true &&
                    timesheet.isCertified === false
                )
            }).sort(sortByDate);

            payPeriods["unsubmitted"] = accountWithTimesheets.timesheets.filter((timesheet) => {
                return (
                    timesheet.isFinal === false &&
                    timesheet.isCertified === false
                )
            });

        }

        //Filter out any submitted timesheets from the allPayPeriods array. 
        let unusedPayPeriods = allPayPeriods.filter((timeslot) => {
            if (
                payPeriods["completed"].filter(payPeriod => payPeriod.weekEndDateObject.isSame(timeslot.weekEndDateObject, "day")).length > 0
                || payPeriods["pending"].filter(payPeriod => payPeriod.weekEndDateObject.isSame(timeslot.weekEndDateObject, "day")).length > 0
                || payPeriods["unsubmitted"].filter(payPeriod => payPeriod.weekEndDateObject.isSame(timeslot.weekEndDateObject, "day")).length > 0
            ) {
                return false
            }
            else {
                return true
            }
        });

        //Add any unusued pay periods to the unsubmitted array    
        payPeriods["unsubmitted"] = payPeriods["unsubmitted"].concat(unusedPayPeriods).sort(sortByDate)

        //Get first element and add 'most recent flag' to it.
        if (payPeriods["unsubmitted"].length > 0) {
            payPeriods["unsubmitted"][0].isMostRecent = true;
        }

        console.log(payPeriods)

        return payPeriods;
    }

    //Return account status if locked or too many submissions are due. Else return false.
    getAccountStatus() {
        if (this.accountStatusChanges.length > 0) {
            //Get most recent account status change  
            let accountStatusChanges = this.accountStatusChanges[this.accountStatusChanges.length - 1];
            return accountStatusChanges;
        }

        return false;
    }

    //Saves/updates object to the api, shows errors or updates state.  
    async save(appStore) {

        const api = ApiMixin(AccountApi);

        let response = await api.save(this);

        //If there are errors, we iterate over them and display them.
        //Then we return false, in case the parent needs to know we failed.

        if (response.errors.length > 0) {
            _.forEach(response.errors, (error) => {
                appStore.addAlert(error);
            });

            return response;
        }


        let newAccountObject;
        //If this already exists in the store, get it we'll be updating it both on api and in store.
        if (this.id) {

            let accountObject = appStore.accounts.getById(this.id);

            //Diff any changes between store and response.
            accountObject.mutate(this);
            accountObject.appStore = appStore;

            newAccountObject = accountObject;

        }
        //If not, we're going to save it to the API, and to push it to the store
        else {
            newAccountObject = new Account(response.data, appStore);

            appStore.addItem({ type: "account", data: newAccountObject });
        }

        //Success alert 
        let successAlert = new Alert("Successfully saved", "Successfully saved account.", "success");
        appStore.addAlert(successAlert);


        //Return the new account object  
        return newAccountObject;
    }

    async addStatusUpdate(values, appStore) {

        const api = AccountApi;


        let response = await api.addStatusUpdate(values);



        appStore.addItem({ type: "accountStatusUpdate", data: { update: values, accountId: this.id } });


        //Success alert 
        let successAlert = new Alert("Successfully saved", "Successfully added new account status.", "success");
        appStore.addAlert(successAlert);


        return this;

    }
}


export default Account;